import { useState } from "react";
import { CircularProgress } from "@mui/material";
import styles from "./RecommenderFooter.module.css";
import {
  handleButtonClickEventBuilder,
  sendEvent,
} from "../../utils/event-helpers";
import { useAppDispatch, useAppSelector } from "../../app/store";
import { usePromptTimer } from "./usePromptTimer";
import RecommenderBackDialog from "../Dialogs/RecommenderBackDialog";
import RecommenderTimeoutDialog from "../Dialogs/RecommenderTimeoutDialog";
import { useNavigate } from "react-router-dom";
import useTenant from "../../tenancy/useTenantPath";

const RecommenderFooter = ({
  enableTimer,
  isPilotParticipant,
}: {
  enableTimer: boolean;
  isPilotParticipant: boolean;
}) => {
  const [openPopup, setOpenPopup] = useState(false);
  const [openTimerPopup, setOpenTimerPopup] = useState(false);
  const [isSendingEvent, setIsSendingEvent] = useState(false);

  const openPopupTimeInSeconds = 3 * 60; // 3 minutes
  const postTestSurveyURL = isPilotParticipant
    ? import.meta.env.VITE_PILOT_POST_SURVEY_URL
    : import.meta.env.VITE_POST_SURVEY_URL;

  const reminderTimer = usePromptTimer({
    durationSeconds: openPopupTimeInSeconds,
    onComplete: () => {
      setOpenTimerPopup(true);
    },
  });

  const { userId, sessionId } = useAppSelector((state) => state.session);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const tenant = useTenant();

  const handlePrevious = () => {
    if (!userId || !sessionId) return;

    const eventData = handleButtonClickEventBuilder(
      userId,
      sessionId,
      "Previous Button",
      {},
    );

    void dispatch(sendEvent(eventData));

    if (enableTimer) setOpenPopup(true);
    else navigate(tenant.basePath + "/mentorpal");
  };

  const handleCloseBackPopup = () => {
    if (!userId || !sessionId) return;

    const eventData = handleButtonClickEventBuilder(
      userId,
      sessionId,
      "Close Qualtrics Reminder Popup",
      {},
    );

    void dispatch(sendEvent(eventData));

    setOpenPopup(false);
  };

  const handleStopTimedPopup = () => {
    setOpenTimerPopup(false);
    reminderTimer.stop();
  };

  const handleSnoozeTimedPopup = () => {
    setOpenTimerPopup(false);
    reminderTimer.reset();
  };

  const handleFinishSurvey = async (logTitle: string) => {
    if (!userId || !sessionId) return;

    try {
      setIsSendingEvent(true);

      const eventData = handleButtonClickEventBuilder(
        userId,
        sessionId,
        logTitle,
        {},
      );
      await dispatch(sendEvent(eventData));
    } catch (error) {
      console.error("Error sending finish survey event:", error);
    } finally {
      setIsSendingEvent(false);
      window.location.href =
        postTestSurveyURL + "?userid=" + userId;
    }
  };

  return (
    <div className={styles.footerContainer}>
      <div className={styles.footerContent}>
        <button
          className={styles.backButton}
          onClick={handlePrevious}
          data-cy="recommender-footer-back-button"
        >
          Previous
        </button>

        <button
          className={styles.finishButton}
          onClick={() => void handleFinishSurvey("Finish Survey")}
          disabled={isSendingEvent || !enableTimer}
          data-cy="recommender-footer-next-button"
        >
          FINISH SURVEY {isSendingEvent && <CircularProgress size={20} />}
        </button>
      </div>

      <RecommenderBackDialog
        open={openPopup}
        onClose={handleCloseBackPopup}
        onGoToQualtrics={() => void handleFinishSurvey("Go to Qualtrics")}
      />

      <RecommenderTimeoutDialog
        open={openTimerPopup && enableTimer}
        onClose={handleStopTimedPopup}
        onSnooze={handleSnoozeTimedPopup}
        onGoToQualtrics={() => void handleFinishSurvey("Go to Qualtrics")}
      />
    </div>
  );
};

export default RecommenderFooter;
